import React from 'react';
import { css } from '@emotion/react';
import Checkbox from '@mui/material/Checkbox';
import { useTranslation } from 'react-i18next';
import { CareRecordButtonGroup } from '../mui/radio/Radio';
import StoolHard from '../svg/stoolHard';
import StoolSoft from '../svg/stoolSoft';
import StoolLittleSoft from '../svg/stoolLittleSoft';
import StoolMiddle from '../svg/stoolMiddle';
import StoolWater from '../svg/stoolWater';
import StoolLittleHard from '../svg/stoolLittleHard';
import StoolKorokoro from '../svg/stoolKorokoro';
import { color } from '../../styles';
import { CareRecordFormHook } from '@/hooks/useCareRecordForm';

interface Props {
  form: CareRecordFormHook;
}

const ContentContainer = css`
  width: 480px;
  margin-bottom: 16px;
`;

const AlignCenter = css`
  display: flex;
  align-items: center;
`;

const ContentTitle = css`
  margin-bottom: 2px;
  font-size: 24px;
  font-weight: bold;
  letter-spacing: 0.3em;
  border-bottom: 3px solid ${color.dark_gray};
`;

const InheritedWidth = css`
  width: inherit;
`;

const InnerRowContainer = css`
  height: 72px;
`;

const CheckboxContainer = css`
  display: flex;
  align-items: center;
  height: 40px;
`;

const StyledCheckbox = css`
  padding: 6px;
`;

const CheckboxLabel = (isDisabled: boolean) => css`
  color: ${isDisabled ? color.thin_gray : color.black};
  cursor: ${isDisabled ? 'none' : 'pointer'};
`;

export const CareRecordStoolSection = ({ form }: Props) => {
  const { t } = useTranslation();
  const isDefecationDisabled = !!form.laxativeId && !form.isEmptyLaxativeId;
  const isLeakOutFecesDisabled =
    !form.defecationVolumeType || form.defecationVolumeType === 'bit' || form.defecationVolumeType === 'none';
  const isHardnessDisabled = !form.defecationVolumeType || form.defecationVolumeType === 'none';

  React.useEffect(() => {
    if (isHardnessDisabled) {
      form.setHardnessType(null);
    }
    if (isLeakOutFecesDisabled) {
      form.setIsLeakOutFeces(null);
    } else if (!form.isLeakOutFeces && form.defecationVolumeType !== null) {
      form.setIsLeakOutFeces(false);
    }
  }, [
    isHardnessDisabled,
    isLeakOutFecesDisabled,
    form.defecationVolumeType,
    form.isLeakOutFeces,
    form.setHardnessType,
    form.setIsLeakOutFeces,
  ]);

  return (
    <div css={[ContentContainer]}>
      <div css={[AlignCenter, ContentTitle]}>{t('stool', '排便')}</div>
      <div css={InheritedWidth}>
        <div css={[AlignCenter, InnerRowContainer]}>
          <CareRecordButtonGroup
            data={[
              { key: 'large', value: t('large', '多'), icon: '/images/recordButtonIcon/stool-large.svg' },
              { key: 'medium', value: t('medium', '中'), icon: '/images/recordButtonIcon/stool-medium.svg' },
              { key: 'small', value: t('small', '少'), icon: '/images/recordButtonIcon/stool-small.svg' },
              { key: 'bit', value: t('bit', '付'), icon: '/images/recordButtonIcon/stool-bit.svg' },
              { key: 'none', value: t('none', '無'), icon: '/images/recordButtonIcon/stool-none.svg' },
            ]}
            newValue={form.defecationVolumeType}
            isDisabled={isDefecationDisabled}
            onChange={form.handleChangeDefecationVolumeType}
          />
        </div>
        <div css={[AlignCenter, InnerRowContainer]}>
          <CareRecordButtonGroup
            data={[
              {
                key: 'korokoro',
                value: t('コロコロ', 'コロコロ'),
                iconComponent: StoolKorokoro,
                isNarrowText: true,
              },
              { key: 'hard', value: t('hard', '硬'), iconComponent: StoolHard },
              { key: 'littleHard', value: t('やや硬', 'やや硬'), iconComponent: StoolLittleHard },
              { key: 'middle', value: t('normal', '普通'), iconComponent: StoolMiddle },
              { key: 'soft', value: t('soft', 'やや軟'), iconComponent: StoolSoft },
              { key: 'littleSoft', value: t('mushy', '泥状'), iconComponent: StoolLittleSoft },
              {
                key: 'water',
                value: t('waterly', '水様状'),
                iconComponent: StoolWater,
              },
            ]}
            newValue={form.hardnessType}
            isDisabled={isHardnessDisabled}
            onChange={form.handleChangeHardnessType}
          />
        </div>
        <div css={CheckboxContainer}>
          <Checkbox
            id={form.isDisplayAdvancedForm ? 'detail-leak-out-feces-checkbox' : 'basic-leak-out-feces-checkbox'}
            css={StyledCheckbox}
            disabled={isLeakOutFecesDisabled}
            checked={form.isLeakOutFeces ?? false}
            onClick={form.handleCheckLeakOutFeces}
          />
          <label
            htmlFor={form.isDisplayAdvancedForm ? 'detail-leak-out-feces-checkbox' : 'basic-leak-out-feces-checkbox'}
            css={CheckboxLabel(isLeakOutFecesDisabled)}
          >
            {t('clothesAndSheetsAreContaminated', '衣服・シーツ汚染あり')}
          </label>
        </div>
      </div>
    </div>
  );
};
